<template>
    <div class="privacy-doc">
        <div class="doc-rule"></div>

        <h1 class="page-title">Xigfor Privacy Policy</h1>
        <p class="effective-date">Effective Date: July 20, 2025</p>
        <p class="intro-text">
            XigFour Inc. ("XigFour," "we," "our," "us") provides a mobile and web-based platform that enables peer-to-peer secondhand buying, selling, and content sharing. This Privacy Policy explains how we collect, use, disclose, and protect your information when you use our services.
        </p>

        <div class="doc-rule"></div>

        <section class="content-section">
            <h2 class="section-title">1. Definitions</h2>
            <p class="desc-text"><strong>Personal Information</strong> means any information that relates to an identified or identifiable individual, including but not limited to name, email address, device identifiers, location data, transaction history, and user-generated content.</p>
            <p class="desc-text"><strong>Sensitive Personal Information</strong> includes data such as precise geolocation, payment credentials, government identifiers, and biometric data, as defined under applicable privacy laws.</p>
        </section>

        <div class="doc-rule"></div>

        <section class="content-section">
            <h2 class="section-title">2. Information We Collect</h2>
            <p class="desc-text">We collect the following categories of data:</p>

            <h3 class="sub-title">a. Information You Provide:</h3>
            <ul class="policy-list">
                <li><strong>Account &amp; Profile:</strong> Name, email, phone number, password, profile photo, bio.</li>
                <li><strong>Listings &amp; Content:</strong> Item details, photos, location, comments, messages.</li>
                <li><strong>Payments:</strong> Payment method (masked card info), transaction records.</li>
                <li><strong>Support Communications:</strong> When you contact us directly.</li>
            </ul>

            <h3 class="sub-title">b. Automatically Collected:</h3>
            <ul class="policy-list">
                <li><strong>Device Info:</strong> OS version, device model, IP address, language, crash logs.</li>
                <li><strong>Usage Data:</strong> Clicks, time on platform, navigation flow.</li>
                <li><strong>Location Data:</strong> Approximate or precise (if permitted).</li>
                <li><strong>Cookies &amp; Tracking:</strong> Analytics data from tools like Google Analytics, Firebase.</li>
            </ul>

            <h3 class="sub-title">c. From Third Parties:</h3>
            <ul class="policy-list">
                <li><strong>Identity Verification Providers:</strong> e.g., Stripe, Plaid.</li>
                <li><strong>Analytics &amp; Ad Partners:</strong> For performance insights and personalized promotions.</li>
            </ul>

            <h3 class="sub-title">d. Unauthenticated Usage:</h3>
            <ul class="policy-list">
                <li>We may collect limited data (e.g., IP address, browser type, device identifiers) even when you browse without logging in.</li>
            </ul>
        </section>

        <div class="doc-rule"></div>

        <section class="content-section">
            <h2 class="section-title">3. How We Use Your Information</h2>
            <p class="desc-text">We use collected data to:</p>
            <ol class="policy-list ordered-list">
                <li>Operate and maintain our platform and services</li>
                <li>Process user transactions, listings, and communications</li>
                <li>Detect and prevent fraud or illegal activity</li>
                <li>Improve user experience through personalization and recommendations</li>
                <li>Send service-related notifications, promotional offers (if opted-in)</li>
                <li>Comply with legal obligations</li>
            </ol>
        </section>

        <div class="doc-rule"></div>

        <section class="content-section">
            <h2 class="section-title">4. How We Share Your Information</h2>
            <p class="desc-text">We may share your information with:</p>
            <ul class="policy-list">
                <li><strong>Service Providers:</strong> Hosting, analytics, email, payment, and verification vendors.</li>
                <li><strong>Business Transfers:</strong> In connection with mergers, acquisitions, restructuring.</li>
                <li><strong>Legal Authorities:</strong> When legally required or to respond to lawful requests.</li>
                <li><strong>Aggregated or Anonymized Data:</strong> For reporting and product development.</li>
            </ul>
            <p class="desc-text">All partners are contractually required to maintain data confidentiality.</p>
        </section>

        <div class="doc-rule"></div>

        <section class="content-section">
            <h2 class="section-title">5. User Rights &amp; Data Control</h2>
            <p class="desc-text">Depending on your location (e.g., EU/EEA or California), you may have rights to:</p>
            <ul class="policy-list">
                <li>Access your personal data</li>
                <li>Correct inaccurate or incomplete data</li>
                <li>Delete your data (subject to legal exemptions)</li>
                <li>Restrict or object to data processing</li>
                <li>Port your data to another platform</li>
                <li>Limit use of sensitive info (CPRA)</li>
            </ul>
            <p class="desc-text">You can exercise these rights via App Settings - Privacy, or email: <a href="mailto:info@xigfor.com" class="inline-link">info@xigfor.com</a>.</p>
            <p class="desc-text"><strong>Verification and Timeline:</strong> We may verify your identity using email or account data. We aim to respond within 30 days, unless legally extended.</p>
        </section>

        <div class="doc-rule"></div>

        <section class="content-section">
            <h2 class="section-title">6. Data Retention</h2>
            <p class="desc-text">We retain your data:</p>
            <ul class="policy-list">
                <li>As long as your account is active.</li>
                <li>Message logs: Up to 24 months.</li>
                <li>Legal, tax, and fraud prevention records: Up to 7 years.</li>
            </ul>
            <p class="desc-text">When no longer needed, data is deleted or anonymized.</p>
        </section>

        <div class="doc-rule"></div>

        <section class="content-section">
            <h2 class="section-title">7. User-Generated Content</h2>
            <p class="desc-text">User-generated content (UGC), such as listings, messages, or profile info, may be publicly visible. You are solely responsible for your content. We may remove any UGC that violates our Terms of Use or laws.</p>
        </section>

        <div class="doc-rule"></div>

        <section class="content-section">
            <h2 class="section-title">8. Children's Privacy</h2>
            <p class="desc-text">XigFor is not directed to children under the age of 13. We do not knowingly collect personal data from children. If we become aware of such collection, we will delete it promptly.</p>
        </section>

        <div class="doc-rule"></div>

        <section class="content-section">
            <h2 class="section-title">9. Cookies &amp; Tracking</h2>
            <p class="desc-text">We use cookies and tracking technologies to:</p>
            <ul class="policy-list">
                <li>Analyze app usage</li>
                <li>Store user preferences</li>
                <li>Enhance security</li>
                <li>Personalize content</li>
            </ul>
            <p class="desc-text">You may control cookies via device or browser settings. Disabling may affect app functionality.</p>
        </section>

        <div class="doc-rule"></div>

        <section class="content-section">
            <h2 class="section-title">10. Third-Party Services &amp; SDKs</h2>
            <p class="desc-text">Our platform integrates third-party services (e.g., Stripe, PayPal). These services have their own privacy policies.</p>
        </section>

        <div class="doc-rule"></div>

        <section class="content-section">
            <h2 class="section-title">11. International Data Transfers</h2>
            <p class="desc-text">XigFour's servers are located in the United States. If you access our services outside the U.S., your data may be transferred to and processed in the U.S.</p>
            <p class="desc-text">For users in the EU/UK, we rely on Standard Contractual Clauses (SCCs) to ensure lawful data transfers.</p>
        </section>

        <div class="doc-rule"></div>

        <section class="content-section">
            <h2 class="section-title">12. Policy Updates</h2>
            <p class="desc-text">We may update this Policy occasionally. Material changes will be communicated through app notifications or email. Please review this page regularly.</p>
        </section>

        <div class="doc-rule"></div>

        <section class="content-section">
            <h2 class="section-title">13. Contact Us</h2>
            <p class="desc-text"><strong>XigFour, Inc.</strong></p>
            <p class="desc-text">Email: [<a href="mailto:info@xigfor.com" class="inline-link">info@xigfor.com</a>]</p>
        </section>

        <div class="doc-rule"></div>

        <section class="content-section">
            <h2 class="section-title">14. App Store / Play Store Integration Guidance</h2>
            <ul class="policy-list">
                <li><strong>Apple App Store:</strong> Link this policy in app metadata and App Settings - Legal - Privacy.</li>
                <li><strong>Google Play:</strong> Add this link to the Privacy Policy field and within app settings.</li>
            </ul>
            <p class="policy-version"><strong>Policy Version:</strong> v1.0</p>
        </section>
    </div>
</template>

<script>
export default {
    name: 'PrivacyPolicyContent'
}
</script>

<style scoped>
.privacy-doc {
    width: 100%;
    color: #111;
    font-family: 'Poppins', Arial, sans-serif;
}

.doc-rule {
    width: 100%;
    height: 1px;
    background: #a9a9a9;
    margin: 0 0 18px;
}

.page-title {
    margin: 0 0 10px;
    font-size: 40px;
    line-height: 1.2;
    font-weight: 700;
    color: #393944;
}

.effective-date {
    margin: 0 0 12px;
    font-size: 40px;
    line-height: 1.5;
    font-weight: 700;
    color: #9B9CA5;
}

.intro-text,
.desc-text,
.policy-list li,
.policy-version,
.section-title,
.sub-title,
.policy-list {
    font-family: 'Poppins Light', sans-serif;
    font-size: 26px;
    font-weight: 300;
    line-height: 56px;
    color: #6C6C78;
}

.intro-text,
.desc-text,
.policy-list,
.policy-version {
    margin: 0 0 10px;
}

.content-section {
    margin-bottom: 18px;
}

.section-title {
    margin: 0 0 12px;
}

.sub-title {
    margin: 10px 0 8px;
}

.policy-list {
    padding-left: 22px;
}

.ordered-list {
    padding-left: 24px;
}

.policy-list li {
    margin-bottom: 6px;
}

.inline-link {
    color: #111;
    text-decoration: underline;
    text-underline-offset: 2px;
}

@media (max-width: 900px) {
    .doc-rule {
        margin-bottom: 12px;
    }

    .page-title {
        font-size: 20px;
        font-weight: 700;
        color: #393944;
        margin-bottom: 8px;
    }

    .effective-date {
        font-size: 12px;
        font-weight: 700;
        color: #9B9CA5;
        line-height: 1.7;
    }

    .intro-text,
    .desc-text,
    .policy-list li,
    .policy-version,
    .section-title,
    .sub-title,
    .policy-list {
        font-family: 'Poppins Light', sans-serif;
        font-size: 10px;
        font-weight: 300;
        line-height: 20px;
        color: #6C6C78;
    }

    .section-title {
        margin-bottom: 8px;
    }

    .sub-title {
        margin: 8px 0 6px;
    }

    .policy-list {
        padding-left: 18px;
    }

    .ordered-list {
        padding-left: 20px;
    }

    .content-section {
        margin-bottom: 12px;
    }
}
</style>
